import { Label, ComboBox, Input } from '@components/common/Forms';
import { CommonFilterForm, SelectedLanguageParam } from '@type/filters';
import { useTranslation } from 'next-i18next';
import { useSpokenLanguagesBuilder } from '@hooks/form-helpers/useSpokenLanguagesBuilder';
import { LanguagePill } from '../partials/LanguagePill';


/**
 * @description The spoken languages combobox and the selected languages pills.
 * * **recordFunc** - the 'record' function from useBjForm.
 * * **verticalSpacing** - the spacing between the items. Defaults to 'gap-2'.
 * * **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 */
export const SpokenLanguagesForm = (props: CommonFilterForm) => {
  // Destructure props
  const {
    recordFunc,
    verticalSpacing = 'gap-2',
    idPrefix,
  } = props;

  // Translation
  const { t } = useTranslation('common');


  // Build the spoken languages
  // **********************************************
  const {
    comboboxOptions,
    addLanguage,
    selectedLanguages,
    adjustedLanguageLevels,
    selectedLanguagesString,
    selectedLanguagesFiltersLabel,
    buildSelectedLanguagesFromString,
    changeLanguageLevel,
    deleteLanguage,
  } = useSpokenLanguagesBuilder();


  // Render component
  // **********************************************
  return (
    <div className={`grid ${verticalSpacing}`}>
      {/* Specialization combobox */}
      <Label
        description={t('filters.search.language.label')}
        descriptionHelper={`(${t('global.combobox.box-type')})`}
        descriptionClass="font-bold !mb-2 text-ink-medium"
        className="!mt-0"
        as="div"
      >
        <ComboBox
          placeholder={t('onboarding.step5.example.placeholder')}
          options={comboboxOptions}
          renderList={(item, index, listKey, getItemProps, listStyling) => (
            <li key={listKey} className={`${listStyling} cursor-pointer`} {...getItemProps({ item, index })}>
              {item['name' as keyof typeof item]}
            </li>
          )}
          onSelectedItemChange={(item) => {
            // Add the selected language to show as pill
            if (item) addLanguage(item as SelectedLanguageParam);
          }}
          {...recordFunc('languagesList', {
            id: `${idPrefix || ''}languagesList`,
            removeFromSubmit: true,
          })}
        />
      </Label>


      {/* Selected languages pills list */}
      <div className="mt-5 inline-grid w-auto auto-cols-max justify-items-start gap-2">
        { // Selected languages list. All languages are shown if none are selected (id='')
          selectedLanguages?.length > 0 && selectedLanguages?.map((language) => (
            <LanguagePill
              key={language.id || 'all_languages'}
              language={language}
              languageLevels={adjustedLanguageLevels}
              changeLanguageLevel={changeLanguageLevel}
              deleteLanguage={deleteLanguage}
            />
          ))
        }
      </div>


      {/* Input that collects the filter languages; use the 'data-filters-label' attribute when dealing with variable filter labels */}
      <Input
        type="hidden"
        value={selectedLanguagesString}
        data-filters-label={selectedLanguagesFiltersLabel}
        onInput={(e) => {
          // If the event is not trusted (e.isTrusted = false), it means it was triggered programmatically
          // (ie: bjForm's setFormValues() function)
          if (!e.isTrusted) buildSelectedLanguagesFromString(e);
        }}
        {...recordFunc('spokenLanguages', {
          id: `${idPrefix || ''}spokenLanguages`,
          convertValue: 'commaStringToArray',
        })}
      />
    </div>
  );
};
